<template>
  <div class="vz-individual-order-cargo-general">
    <div class="text-subtitle color-dark mt-20 mb-10">
      Общие габариты
    </div>
    <div v-if="isDimension">
      <div class="vz-individual-order-cargo-general-inputs">
        <vz-measure-input
          v-model.number="generalVolume"
          :disabled="orderLock"
          big
          label="Объем"
          class="small-width vz-individual-order-cargo-general-item"
          :class="volumeDiffClass"
          placeholder="0.01"
          :max-value="maxGeneralVolume"
          :min-value="0.01"
          :measure="m3"
          with-max-notice
          @change="onChangeItemParam"
        />
        <vz-measure-input
          v-model.number="generalWeight"
          :disabled="orderLock"
          big
          lazy
          class="small-width vz-individual-order-cargo-general-item"
          :class="weightDiffClass"
          placeholder="0.9"
          measure="кг"
          :max-value="maxGeneralWeight"
          :min-value="0.1"
          :mask-decimal-limit="1"
          with-max-notice
          @change="onChangeWeightAndPlace"
        >
          <template #label>
            <span class="vz-input-label">Вес</span>
            <vz-warning-button
              v-if="dimensionsMod.weight()"
              ref="overloadWarnings"
              :toast-data="[overloadToast]"
              :hidden="orderStore.hideOrderWrappingOverload"
              @update:hidden="orderStore.hideOrderWrappingOverload = $event"
            />
          </template>
        </vz-measure-input>
        <vz-measure-input
          v-model.number="generalCount"
          :disabled="orderLock"
          label="Мест"
          class="small-width"
          placeholder="1"
          big
          lazy
          measure="шт"
          :max-value="999"
          :min-value="0"
          :allow-decimal="false"
          :rules="countRules"
          with-max-notice
          @change="onChangeWeightAndPlace"
        />
      </div>
      <div v-if="volumeDiff || weightDiff" class="text-caption color-primary mt-5">
        {{ diffChecksText }}
      </div>
    </div>
    <div v-else class="flex flex-wrap">
      <div class="vz-individual-order-cargo-general-data" :class="{error: generalVolume > maxGeneralVolume}">
        <span>Объем</span>
        <div :class="{'color-inactive': orderLock}">
          {{ generalVolume }}
          <span>м<sup>3</sup></span>
        </div>
        <span v-if="generalVolume > maxGeneralVolume" class="vz-invalid-message color-primary fs-12">
          Не более {{ maxGeneralVolume }} м<sup style="top: 0.25rem">3</sup>
        </span>
      </div>
      <div class="vz-individual-order-cargo-general-data" :class="{error: generalWeight > maxGeneralWeight}">
        <span>Вес</span>
        <div :class="{'color-inactive': orderLock}">
          {{ generalWeight }}
          <span>кг</span>
        </div>
        <span v-if="generalWeight > maxGeneralWeight" class="vz-invalid-message color-primary fs-12">
          Не более {{ maxGeneralWeight }} кг
        </span>
      </div>
      <div class="vz-individual-order-cargo-general-data">
        <span >Мест</span>
        <div :class="{'color-inactive': orderLock}">
          {{ generalCount }}
          <span>шт</span>
        </div>
      </div>
      <!--      Невидимый инпут для невалидности заказа-->
      <vz-input
        v-if="generalVolume > maxGeneralVolume || generalWeight > maxGeneralWeight"
        :model-value="1"
        :rules="[formRules.required, {...formRules.min, param: 10}]"
        style="display: none"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {useOrderStore} from '~/stores/order';
import {parcelTypes} from '~/common/objects';
import {modChecker, OrderWrappingModParameters, ToastConfigureObject} from '~/types/types'
import {useWrappingStore} from '~/stores/wrapping';
import {getModCheckFunction} from '~/common/functions';
import VzOrderButton from '~/components/ui-kit/vz-warning-button.vue'
import VzMeasureInput from '~/components/ui-kit/vz-measure-input.vue'
import VzInput from '~/components/ui-kit/vz-input.vue';
import formRules from '~/common/vz-form-rules';
import VzWarningButton from '~/components/ui-kit/vz-warning-button.vue';

const emit = defineEmits([
  'set-mode'
])

const props = defineProps({
  orderLock: {
    type: Boolean,
    default: false
  }
})

const orderStore = useOrderStore()
const wrappingStore = useWrappingStore()
const overloadWarnings = ref({} as typeof VzOrderButton)

const m3 = '<span>м<sup>3</sup></span>'
const minVolume = 0.01
const modWeightDetail = ref(false)

const mode = computed(() => {
  return orderStore.orderForm.cargo.mode
})

const isDimension = computed(() => {
  return mode.value === 'dimension'
})

const weightDiff = computed(() => {
  return Number(orderStore.orderForm.cargo.dimensions.detail.weight) > Number(orderStore.orderForm.cargo.dimensions.general.weight)
})

const volumeDiff = computed(() => {
  return Number((orderStore.orderForm.cargo.dimensions.detail.length *
    orderStore.orderForm.cargo.dimensions.detail.width *
    orderStore.orderForm.cargo.dimensions.detail.height).toFixed(2)) > Number(orderStore.orderForm.cargo.dimensions.general.volume)
})

const volumeDiffClass = computed(() => {
  return volumeDiff.value && isDimension.value ? 'general-item-diff' : ''
})

const weightDiffClass = computed(() => {
  return weightDiff.value && isDimension.value ? 'general-item-diff' : ''
})

const isOnlyCorrespondence = computed(() => {
  return orderStore.orderForm.cargo.type.correspondence && !orderStore.orderForm.cargo.type.cargo
})

const diffChecksText = computed(() => {
  const text = volumeDiff.value && weightDiff.value
    ? 'объем и вес'
    : (volumeDiff.value
      ? 'объем'
      : (weightDiff.value
        ? 'вес'
        : ''))
  return 'Общий ' + text + ' меньше максимальных габаритов. Стоимость перевозки может измениться.'
})

const countRules = computed(() => {
  if (props.orderLock || isOnlyCorrespondence.value) {
    return undefined
  }
  return [{...formRules.min, param: 1, customMessage: 'Заполните'}, {...formRules.required, customMessage: 'Заполните'}]
})

const generalVolume = computed({
  get() {
    let volume = 0
    if (mode.value === 'dimension') {
      volume = orderStore.orderForm.cargo.dimensions.general.volume
    } else {
      // todo any
      volume = (orderStore.orderForm.cargo[mode.value].items as any).reduce((preVal: number, item: any) => {
        if (mode.value === 'places' || mode.value === 'pallets') {
          preVal += (item.length * item.width * item.height * item.count)
        } else if (mode.value === 'parcels') {
          preVal += parcelTypes[item.type].volume * parseInt(item.count)
        }
        return preVal
      }, 0).toFixed(2)
      volume = (volume < minVolume) ? minVolume : volume
    }
    return +volume
  },
  set(volume: number) {
    if (mode.value === 'dimension') {
      orderStore.orderForm.cargo.dimensions.general.volume = volume
    }
  }
})

const generalWeight = computed({
  get (): number {
    let weight = 0
    if (mode.value === 'dimension') {
      weight = orderStore.orderForm.cargo.dimensions.general.weight
    } else {
      weight = (orderStore.orderForm.cargo[mode.value].items as any).reduce(function (preVal: number, item: any) {
        preVal += parseFloat(item.weight) * parseInt(item.count)
        return preVal
      }, 0).toFixed(1)
    }
    return weight
  },
  set(weight: number) {
    if (mode.value === 'dimension') {
      orderStore.orderForm.cargo.dimensions.general.weight = weight
    }
  }
})

const generalCount = computed({
  get() {
    let count: number|null = 0
    if (mode.value === 'dimension') {
      count = orderStore.orderForm.cargo.dimensions.general.place
    } else {
      count = (orderStore.orderForm.cargo[mode.value].items as any).reduce((preVal: number, item: any) => {
        preVal += parseInt(item.count)
        return preVal
      }, 0)
    }
    return count
  },
  set(count: number|null) {
    if (mode.value === 'dimension') {
      orderStore.orderForm.cargo.dimensions.general.place = count || null
    }
  }
})

const maxGeneralWeight = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.general?.weight?.['<='] || 19999
})

const maxGeneralVolume = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.general?.volume?.['<='] || 79.9
})

const dimensionsMod = computed((): modChecker => {
  const mod: modChecker = {
    weight: () => false,
    weightProperty: '',
    weightMessage: ''
  }

  const weight = wrappingStore.mods.find(item => item.mod.condition.cargo?.dimension?.detail.weight)
  if (weight) {
    const weightValue = Object.values(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0]
    mod.weight = (): boolean => {
      const fc = getModCheckFunction(Object.keys(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0] as OrderWrappingModParameters['conditions'])
      const general = fc(parseInt((orderStore.orderForm.cargo.dimensions.general.weight) as never as string), weightValue)
      modWeightDetail.value = fc(parseInt(orderStore.orderForm.cargo.dimensions.detail.weight as never as string), weightValue)
      return general
    }
    mod.weightProperty = weight.key
    mod.value = weightValue
    mod.weightMessage = weight.mod.message
  }

  return mod
})

const overloadToast = computed((): ToastConfigureObject => {
  const dimensionsModWrapping = orderStore.orderForm.cargo.dimensions.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  return {
    variant: 'primary',
    title: 'Постановка на паллет',
    text: overloadText(),
    autocloseDelay: 20000,
    cancelButtonText: dimensionsModWrapping ? undefined : 'Не нужно',
    buttonText: dimensionsModWrapping ? undefined : 'Воспользоваться',
    buttonCB: dimensionsModWrapping ? undefined : addModsWrapping,
    //
    footerText: dimensionsModWrapping ? 'Упаковка добавлена' : undefined,
    footerVariant: dimensionsModWrapping ? 'success' : undefined,
    footerIconName: dimensionsModWrapping ? 'check-all' : undefined
  }
})

function validateWeight() {
  const dimensionsModWrapping = orderStore.orderForm.cargo.dimensions.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  // if (dimensionsMod.value.weight() && !dimensionsModWrapping) {
  // Тост и так появится из-за максимальных габаритов
  // if (!(isDimension.value && orderStore.orderForm.cargo.dimensions.general.place === 1)) {
  //   this.vzOrderWarnings?.showToast()
  // }
  // }

  // Если нет перегруза по местам и в общих габаритах, то снимаем с паллеты
  if (!dimensionsMod.value.weight() && !modWeightDetail.value && dimensionsModWrapping) {
    orderStore.orderForm.cargo.dimensions.wrapping =
      orderStore.orderForm.cargo.dimensions.wrapping.filter(item => item.code !== dimensionsMod.value.weightProperty)
  }
}

function addModsWrapping() {
  const dimensionsModWrapping = orderStore.orderForm.cargo.dimensions.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  if (!dimensionsModWrapping) {
    orderStore.orderForm.cargo.dimensions.wrapping.push({ code: dimensionsMod.value.weightProperty, value: 1 })
    orderStore.getOrderPriceAction()
  }
}

function onChangeWeightAndPlace() {
  if (isDimension.value) {
    validateWeight()
  }
  orderStore.getOrderPriceAction()
}

function onChangeItemParam() {
  orderStore.getOrderPriceAction()
}

function setMode(value: string) {
  emit('set-mode', value)
}

function overloadText(): any {
  return [
    h('span', 'Если Ваш груз располагается на паллете, рекомендуем воспользоваться '),
    h('a',
      {
        href: '#pallets',
        onClick: (e: Event) => {
          e.preventDefault()
          setMode('pallets')
          overloadWarnings.value?.hide()
        }
      },
      '«Вводом груза по паллетам»'
    ),
    h('span', `. Обратите внимание! Для грузов с весом одного места больше
       ${dimensionsMod.value.value} кг услуга упаковки «Постановка на паллет» обязательна.`)
  ]
}
</script>
